import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SecondaryButton } from '@pulse-web-ui/button-v2';
import { WidgetContainer } from '@pulse-web-ui/containers';

import { sendAnalyticEvent } from '@app/web-analytic';

import type { MVPProfile, Profile } from '@entities/agent-contract';

import {
  AddendumStatus,
  AnalyticEvent,
  EDIT_PROFILE,
  ProfileSection,
} from '@shared/index';

import { getContacts, profileSectionToAnalyticEvent } from '../../utils';
import { AddressSection } from '../address-section';
import { BankDetailsSection } from '../bank-details-section';
import { ContactsSection } from '../contacts-section';
import { DraftAlert } from '../draft-alert';
import { DraftStateBlock } from '../draft-state-block';
import { MVPDataSection } from '../mvp-data-section';
import { PersonalDataSection } from '../personal-data-section';
import { ProfileDocuments } from '../profile-documents';
import { AgentProfileContainer } from './agent-profile-container';

interface AgentProfileConfirmProps {
  profile: Profile;
  mvpProfile?: MVPProfile;
  profileAddendumStatus?: AddendumStatus;
  isDisplayEditButtons: boolean;
}

export const AgentProfileConfirm = ({
  profile,
  mvpProfile,
  profileAddendumStatus,
  isDisplayEditButtons,
}: AgentProfileConfirmProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addressesAgents, bankDetails } = profile;
  const contacts = getContacts(profile);

  const getButtonProps = useCallback(
    (profileSection: ProfileSection) => {
      if (isDisplayEditButtons) {
        const isEditable =
          profileAddendumStatus === AddendumStatus.DRAFT ||
          !profileAddendumStatus;

        return {
          disabled: !isEditable,
          onClick: () => {
            sendAnalyticEvent(AnalyticEvent.AGENT_TOSTEP_EDITPERSONALDATA, {
              category: profileSectionToAnalyticEvent[profileSection],
            });

            navigate(`${EDIT_PROFILE}/${profileSection}`);
          },
        } as React.ComponentProps<typeof SecondaryButton>;
      }
    },
    [isDisplayEditButtons, profileAddendumStatus]
  );

  const renderDraftAlert = useCallback(
    (isAlredyRendered?: boolean) =>
      !isAlredyRendered &&
      profileAddendumStatus === AddendumStatus.AWAIT_COMPANY && <DraftAlert />,
    [profileAddendumStatus]
  );

  return (
    <AgentProfileContainer label={t('AGENT_PROFILE:headers.profile')}>
      {profileAddendumStatus === AddendumStatus.DRAFT && <DraftStateBlock />}

      {mvpProfile && (
        <WidgetContainer>
          {renderDraftAlert()}
          <MVPDataSection
            profile={profile}
            mvpProfile={mvpProfile}
          />
        </WidgetContainer>
      )}
      <WidgetContainer>
        {renderDraftAlert(!!mvpProfile)}
        <PersonalDataSection
          profile={profile}
          buttonProps={getButtonProps(ProfileSection.PERSONAL_DATA)}
        />
      </WidgetContainer>
      <WidgetContainer>
        <ContactsSection
          contacts={contacts}
          isRenderTitle
          buttonProps={getButtonProps(ProfileSection.CONTACTS)}
        />
      </WidgetContainer>
      <WidgetContainer>
        <AddressSection
          addresses={addressesAgents}
          isRenderTitle
          buttonProps={getButtonProps(ProfileSection.ADDRESS)}
        />
      </WidgetContainer>
      <WidgetContainer>
        <BankDetailsSection
          bankDetails={bankDetails}
          isRenderTitle
          buttonProps={getButtonProps(ProfileSection.BANK_DETAILS)}
        />
      </WidgetContainer>
      <ProfileDocuments />
    </AgentProfileContainer>
  );
};
